// app.luwu.dev/s/<code>: watch (or type in) a terminal window someone shared,
// or see and control a computer's screen (a screen share, through noVNC).

import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import type { TermHost } from "@luwu/proto";
import RFB from "@novnc/novnc";
import type { PrivateKey } from "@libp2p/interface";
import { codeFrom, join, JoinError, open, openScreen, type JoinInfo, type ScreenSession, type Session, type Status } from "./luwu";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const views = ["join", "connecting", "terminal", "ended"] as const;
type View = (typeof views)[number];

function show(v: View) {
  for (const id of views) $(`view-${id}`).hidden = id !== v;
  document.body.dataset.view = v;
}

const stored = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};
const store = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    // private mode: fine
  }
};

const fmtCode = (c: string) => {
  const n = c.toUpperCase().replace(/[^0-9A-Z]/g, "");
  return n.length === 12 ? `${n.slice(0, 4)}-${n.slice(4, 8)}-${n.slice(8)}` : c;
};
const time = (unix: number) => new Date(unix * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// app.luwu.dev itself is the console (sign in, computers, terminals); share
// links (/s/<code>, /?code=) stay here.
if (location.pathname === "/" && !new URLSearchParams(location.search).has("code")) location.replace(`/computers${location.search}`);

const code = codeFrom(location.pathname.startsWith("/s/") ? location.pathname : new URLSearchParams(location.search).get("code") ?? "");
const codeInput = $<HTMLInputElement>("code");
const nameInput = $<HTMLInputElement>("name");
codeInput.value = code ? fmtCode(code) : "";
nameInput.value = stored("luwu-name");
$<HTMLAnchorElement>("open-app").href = `luwu://join?code=${encodeURIComponent(code || codeInput.value)}`;
codeInput.addEventListener("input", () => {
  $<HTMLAnchorElement>("open-app").href = `luwu://join?code=${encodeURIComponent(codeInput.value.trim())}`;
});
if (!code) codeInput.focus();
else nameInput.focus();

const steps: Record<Status | "join" | "screen", string> = {
  join: "Getting access…",
  relay: "Reaching the computer…",
  auth: "Checking its identity…",
  terminal: "Opening the terminal…",
  screen: "Opening the screen…",
};

let session: Session | ScreenSession | null = null;
let term: Terminal | null = null;

$<HTMLFormElement>("join-form").addEventListener("submit", (e) => {
  e.preventDefault();
  void start();
});

async function start() {
  const c = codeFrom(codeInput.value);
  const name = nameInput.value.trim();
  const err = $("join-error");
  err.textContent = "";
  if (c.replace(/[^0-9a-z]/gi, "").length < 12) {
    err.textContent = "Enter the 12-character code from the link you were sent.";
    return;
  }
  if (!name) {
    err.textContent = "Add your name. The person sharing sees it.";
    nameInput.focus();
    return;
  }
  store("luwu-name", name);
  show("connecting");
  $("step").textContent = steps.join;
  let info: JoinInfo;
  let key;
  try {
    ({ key, info } = await join(c, name));
  } catch (e) {
    show("join");
    err.textContent =
      e instanceof JoinError && e.status === 404
        ? "No share has that code. Check it, or ask for a new link."
        : e instanceof JoinError && e.status === 410
          ? "This share has ended. Ask for a new link."
          : (e as Error).message;
    return;
  }
  if (history.replaceState && !location.pathname.startsWith("/s/")) history.replaceState(null, "", `/s/${c.replace(/-/g, "")}`);
  if (info.share.kind === "screen") return startScreen(key, info);
  const who = info.share.owner_name || "Someone";
  $("t-title").textContent = `${who} · ${info.share.window_label || "a terminal"}`;
  $("t-mode").textContent = info.share.can_type ? "You can type" : "View only";
  $("t-mode").dataset.mode = info.share.can_type ? "type" : "view";
  $("t-until").textContent = `until ${time(info.share.expires_at)}`;

  // The terminal, sized to the page; zoomed out when the shared window is bigger.
  term = new Terminal({
    fontFamily: '"IBM Plex Mono", ui-monospace, Menlo, Consolas, monospace',
    fontSize: 14,
    cursorBlink: info.share.can_type,
    disableStdin: !info.share.can_type,
    scrollback: 5000,
    theme: { background: "#0E1525", foreground: "#E6E2D6", cursor: "#C9A35A", selectionBackground: "#C9A35A55" },
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  // xterm measures its character cells when it opens: with the web font not
  // loaded yet it measures the fallback font and keeps that until a resize.
  // Wait for the font (briefly), and re-measure once all fonts are in.
  await Promise.race([document.fonts.load('14px "IBM Plex Mono"').catch(() => {}), new Promise((r) => setTimeout(r, 1500))]);
  show("terminal");
  term.open($("term"));
  fit.fit();
  let win: { cols: number; rows: number } | null = null;
  // A guest never sizes the shared window, so show exactly its size: zoomed
  // out when it's bigger than the page, at normal size when it's smaller
  // (otherwise tmux pads the difference with dots).
  const layout = () => {
    if (!term) return;
    term.options.fontSize = 14;
    const nat = fit.proposeDimensions();
    if (win && nat) {
      const scale = Math.min(1, nat.cols / win.cols, nat.rows / win.rows);
      term.options.fontSize = Math.max(6, Math.floor(14 * scale * 10) / 10);
      term.resize(win.cols, win.rows);
    } else {
      fit.fit();
    }
    if (session && "resize" in session) session.resize(term.cols, term.rows);
  };
  new ResizeObserver(() => layout()).observe($("term"));
  void document.fonts.ready.then(() => {
    if (!term) return;
    term.options.fontSize = 13; // a font-size change makes xterm measure again
    layout();
  });

  const onMessage = (m: TermHost) => {
    switch (m.m.case) {
      case "data":
        term?.write(m.m.value.data);
        break;
      case "windowSize":
        win = { cols: m.m.value.cols, rows: m.m.value.rows };
        layout();
        break;
      case "exit":
        end(m.m.value.reason);
        break;
    }
  };
  try {
    session = await open(key, info, { cols: term.cols, rows: term.rows }, onMessage, (s) => ($("t-path").textContent = steps[s]));
  } catch (e) {
    end((e as Error).message);
    return;
  }
  $("t-path").textContent = session.path;
  layout();
  const term_ = session as Session; // the bar above may have wrapped while connecting
  if (info.share.can_type) {
    const enc = new TextEncoder();
    term.onData((d) => term_.input(enc.encode(d)));
    term.onBinary((d) => term_.input(Uint8Array.from(d, (ch) => ch.charCodeAt(0))));
    term.focus();
  }
}

/** A screen share: noVNC over the forward stream, scaled to the page. */
async function startScreen(key: PrivateKey, info: JoinInfo) {
  const who = info.share.owner_name || "Someone";
  $("t-title").textContent = `${who} · screen`;
  $("t-mode").textContent = "You can control";
  $("t-mode").dataset.mode = "type";
  $("t-until").textContent = `until ${time(info.share.expires_at)}`;
  $("term").hidden = true;
  $("screen").hidden = false;
  $("ended-title").textContent = "Shared screen closed";
  show("terminal");
  let s: ScreenSession;
  try {
    s = await openScreen(key, info, (st) => ($("t-path").textContent = steps[st]));
  } catch (e) {
    end((e as Error).message);
    return;
  }
  session = s;
  $("t-path").textContent = s.path;
  const rfb = new RFB($("screen"), s.channel);
  rfb.scaleViewport = true;
  rfb.resizeSession = false;
  rfb.focusOnClick = true;
  rfb.background = "#000";
  const form = $<HTMLFormElement>("vnc-auth");
  rfb.addEventListener("credentialsrequired", (e: CustomEvent<{ types: string[] }>) => {
    const needUser = e.detail.types.includes("username");
    $("vnc-user-row").hidden = !needUser;
    $("vnc-auth-hint").textContent = needUser
      ? "Its account name and password, as at its login screen. They go straight to the computer, encrypted end to end."
      : "The Screen Sharing password set on the computer.";
    form.hidden = false;
    (needUser ? $<HTMLInputElement>("vnc-user") : $<HTMLInputElement>("vnc-pass")).focus();
  });
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    form.hidden = true;
    const pass = $<HTMLInputElement>("vnc-pass");
    rfb.sendCredentials({ username: $<HTMLInputElement>("vnc-user").value, password: pass.value });
    pass.value = "";
  });
  rfb.addEventListener("connect", () => {
    $("t-path").textContent = `${s.path} · connected`;
    rfb.focus();
  });
  rfb.addEventListener("securityfailure", (e: CustomEvent<{ reason?: string }>) => end(`Sign-in failed${e.detail.reason ? `: ${e.detail.reason}` : ". Check the name and password."}`));
  rfb.addEventListener("disconnect", (e: CustomEvent<{ clean: boolean }>) => end(e.detail.clean ? "You left the shared screen." : "connection lost"));
  $("leave").addEventListener("click", () => rfb.disconnect(), { once: true });
}

let ended = false;
function end(reason: string) {
  if (ended) return;
  ended = true;
  void session?.close();
  session = null;
  const r = reason.toLowerCase();
  $("ended-reason").textContent =
    r === "connection lost" || r.includes("detached") || r.includes("exited")
      ? "The connection closed. The share may have ended, or the computer went offline."
      : reason;
  show("ended");
}

$("leave").addEventListener("click", () => end($("screen").hidden ? "You left the shared terminal." : "You left the shared screen."));
$("rejoin").addEventListener("click", () => location.reload());
show("join");
